import Vue from 'vue'
import uid from '@/nms/components/utils/uid'
import { $_bridge } from '@/nms/plugins/bridge.js'
const pendingCreatedMap = new Map<string, boolean>()

// Типы
interface UploadItemData {
  id: string
  queue_id: string
  total: {
    bytes: number
  }
  loaded: {
    bytes: number
    percents: number
  }
  callback?: {
    pending?: (item: any) => void
    progress?: (item: any, progress: any) => void
    success?: (item: any, response: any) => void
    warning?: (item: any, message: string) => void
    error?: (item: any, error: any) => void
  }
  name?: string
  size?: number
  file?: File
  data?: any
  [key: string]: any
}

interface UploadQueue {
  id: string
  total: {
    bytes: number
    items: number
  }
  loaded: {
    bytes: number
    items: number
  }
  data: Record<string, UploadItemData>
  method?: string
  abortControllers?: Record<string, AbortController>
}

interface UploadState {
  queue: Record<string, UploadQueue>
  counter: string | null
}

// Начальное состояние
const getDefaultState = (): UploadState => ({
  queue: {},
  counter: null
})

const state = getDefaultState()

// Мутации
const mutations = {
  SET_QUEUE_DATA(state: UploadState, [queueId, data]: [string, UploadQueue]) {
    Vue.set(state.queue, queueId, data)
  },

  SET_QUEUE_TOTAL_ITEMS(state: UploadState, { queue_id, total }: { queue_id: string; total: number }) {
    if (state.queue[queue_id]) {
      Vue.set(state.queue[queue_id].total, 'items', total)
    }
  },

  INCREMENT_QUEUE_LOADED_ITEMS(state: UploadState, { queue_id }: { queue_id: string }) {
    if (state.queue[queue_id]) {
      const newItemsCount = state.queue[queue_id].loaded.items + 1
      Vue.set(state.queue[queue_id].loaded, 'items', newItemsCount)
    }
  },

  INCREMENT_QUEUE_TOTAL_BYTES(state: UploadState, { queue_id, bytes }: { queue_id: string; bytes: number }) {
    if (state.queue[queue_id]) {
      const newBytes = state.queue[queue_id].total.bytes + bytes
      Vue.set(state.queue[queue_id].total, 'bytes', newBytes)
    }
  },

  UPDATE_QUEUE_ITEM_PROGRESS(state: UploadState, { queueId, fileId, loaded, total }: { queueId: string; fileId: string; loaded: number; total: number }) {
    const queue = state.queue[queueId]
    if (queue?.data[fileId]) {
      const percent = Math.round((loaded * 100) / total)
      Vue.set(queue.data[fileId].loaded, 'bytes', loaded)
      Vue.set(queue.data[fileId].loaded, 'percents', percent)
      Vue.set(queue.data[fileId].total, 'bytes', total)
    }
  },

  CLEAR_QUEUE_DATA(state: UploadState, { queue_id }: { queue_id: string }) {
    Vue.delete(state.queue, queue_id)
  },

  CHANGE_COUNTER(state: UploadState, { $id }: { $id: string }) {
    state.counter = $id
  }
}

// Геттеры
const getters = {
  queues: (state: UploadState) => state.queue,
  
  queue: (state: UploadState) => (queueId: string) => state.queue[queueId],
  
  queueProgress: (state: UploadState) => (queueId: string) => {
    const queue = state.queue[queueId]
    if (!queue || queue.total.bytes === 0) return 0
    return Math.round((queue.loaded.bytes * 100) / queue.total.bytes)
  },
  
  queueItemsCount: (state: UploadState) => (queueId: string) => {
    const queue = state.queue[queueId]
    return queue ? Object.keys(queue.data).length : 0
  },
  
  getCounter: (state: UploadState) => state.counter
}

// Вспомогательные функции для уведомлений (как в download.ts)
const notifications = {
  // Убираем pending, сразу показываем progress с 0%
  uploading: (item: UploadItemData) => {
    return `<div class="fit text-xs-left">
      <span class="text-truncate block">${item.name || 'Файл'}</span>
    </div>
    <div class="caption fit text-xs-left">
      <span class="text-truncate">Файл загружается...</span>
    </div>
    <div role="progressbar" aria-valuemin="0" aria-valuemax="100" class="v-progress-linear mb-0" style="height: 4px;">
      <div class="v-progress-linear__background primary" style="height: 4px; opacity: 0.3; width: 100%;"></div>
      <div class="v-progress-linear__bar">
        <div class="v-progress-linear__bar__indeterminate v-progress-linear__bar__indeterminate--active">
          <div class="v-progress-linear__bar__indeterminate long primary"></div>
          <div class="v-progress-linear__bar__indeterminate short primary"></div>
        </div>
      </div>
    </div>`
  },

  progress: (item: UploadItemData, progress: number) => {
    return `<div class="fit text-xs-left">
      <span class="text-truncate block">${item.name || 'Файл'}</span>
    </div>
    <div class="caption fit text-xs-left">
      <span class="text-truncate">Файл загружен на ${progress}%</span>
    </div>
    <div role="progressbar" aria-valuemin="0" aria-valuemax="100" class="v-progress-linear mb-0" style="height: 4px;">
      <div class="v-progress-linear__background primary" style="height: 4px; opacity: 0.3; width: 100%;"></div>
      <div class="v-progress-linear__bar">
        <div class="v-progress-linear__bar__determinate accent" style="width: ${progress}%;"></div>
      </div>
    </div>`
  },

  success: (item: UploadItemData) => {
    return `<div class="fit text-xs-left">
      <span class="text-truncate block">${item.name || 'Файл'}</span>
    </div>
    <div class="caption fit text-xs-left">
      <span class="text-truncate success--text">✓ Файл успешно загружен!</span>
    </div>`
  },

  error: (item: UploadItemData, errorMsg: string) => {
    return `<div class="fit text-xs-left">
      <span class="text-truncate block">${item.name || 'Файл'}</span>
    </div>
    <div class="caption fit text-xs-left">
      <span class="text-truncate error--text">✗ Ошибка: ${errorMsg}</span>
    </div>`
  }
}

// Действия
const actions = {
  /**
   * Execute upload for multiple files
   */
  async execute({ commit, state }: any, { 
    filetype, 
    $id, 
    list, 
    vm, 
    method, 
    params 
  }: { 
    filetype?: string; 
    $id?: string; 
    list: any[]; 
    vm: any; 
    method?: string; 
    params?: { type?: string; password?: string }
  }) {
    
    return new Promise(async (resolve, reject) => {
      const queueId = uid('queue')
      const abortControllers: Record<string, AbortController> = {}
      
      try {
        // 1. Создаём карту файлов
        const items: Record<string, UploadItemData> = {}
        for (const item of list) {
          const fileId = uid('file')
          items[fileId] = {
            id: fileId,
            queue_id: queueId,
            total: { bytes: 0 },
            loaded: { bytes: 0, percents: 0 },
            file: item,
            name: item.name,
            size: item.size,
            type: item.type,
            data: item.data || {},
            callback: item.callback || null
          }
        }
        
        // 2. Устанавливаем очередь
        const queue: UploadQueue = {
          id: queueId,
          total: { bytes: 0, items: list.length },
          loaded: { bytes: 0, items: 0 },
          data: items,
          method: method || 'formData',
          abortControllers
        }
        
        commit('SET_QUEUE_DATA', [queueId, queue])
        commit('SET_QUEUE_TOTAL_ITEMS', { queue_id: queueId, total: list.length })
        
        // 3. Вызываем колбэк pending для каждого файла (как в download.ts)
        Object.keys(items).forEach(fileId => {
          const item = items[fileId]
          if (item.callback?.pending) {
            item.callback.pending(item)
          } else {
            let file = null
            if (item instanceof File) {
              file = item
            } else if (item.file instanceof File) {
              file = item.file
            } else if (item.raw instanceof File) {
              file = item.raw
            } 
            if(file) {
              this.dispatch('file/upload/notifyPending', { item, vm }, { root: true })
            }
          }
        })
        
        // 4. Выбираем метод загрузки
        const uploadMethod = method || 'formData'
        console.log('NEW UPLOAD', 'uploadMethod', uploadMethod)
        
        if (uploadMethod === 'arrayBuffer') {
          await this.dispatch('file/upload/uploadArrayBuffer', {
            queueId,
            $id,
            filetype,
            vm,
            params
          }, { root: true })
        } else {
          await this.dispatch('file/upload/uploadFormData', {
            queueId,
            $id,
            filetype,
            vm
          }, { root: true })
        }
        
        resolve({ success: true, queueId })
        
      } catch (error) {
        console.error('Upload queue execution failed:', error)
        reject(error)
      }
    })
  },
  
  /**
   * Upload files using FormData (multipart/form-data)
   */
  async uploadFormData({ commit, state }: any, { 
    queueId, 
    $id, 
    filetype, 
    vm 
  }: { 
    queueId: string; 
    $id?: string; 
    filetype?: string; 
    vm: any 
  }) {
    const queue = state.queue[queueId]
    if (!queue) return
    
    const uploadPromises = Object.keys(queue.data).map(async (fileId) => {
      const item = queue.data[fileId]
      
      // Достаём файл из обёртки
      let file = null
      
      if (item instanceof File) {
        file = item
      } else if (item.file instanceof File) {
        file = item.file
      } else if (item.raw instanceof File) {
        file = item.raw
      } else {
        console.error('No File object found in item:', item)
        return
      }
      
      const abortController = new AbortController()
      
      if (queue.abortControllers) {
        queue.abortControllers[fileId] = abortController
      }
      
      try {
        let obj$
        if ($id) {
          obj$ = await $_bridge.getObjectById($id)
        }
        
        const formData = new FormData()
        formData.append('files', file)
        
        let response
        
        if (filetype) {
          response = await $_bridge.sendFileToAlarm(obj$.$id, formData, filetype)
        } else {
          response = await obj$.update(formData, {
            onUploadProgress: (progressEvent: any) => {
              const totalLength = progressEvent.lengthComputable
                ? progressEvent.total
                : progressEvent.target?.getResponseHeader('content-length')
                  || progressEvent.target?.getResponseHeader('x-decompressed-content-length')
              
              if (totalLength) {
                const progress = Math.round((progressEvent.loaded * 100) / totalLength)
                
                // Обновляем прогресс в store
                commit('UPDATE_QUEUE_ITEM_PROGRESS', {
                  queueId,
                  fileId,
                  loaded: progressEvent.loaded,
                  total: totalLength
                })
                
                // Вызываем колбэк прогресса или уведомление (как в download.ts)
                if (item.callback?.progress) {
                  item.callback.progress(item, { length: totalLength, loaded: progress })
                } else {
                  this.dispatch('file/upload/notifyProgress', { item, progress, vm }, { root: true })
                }
              }
            }
          })
        }
        
        if (response) {
          commit('INCREMENT_QUEUE_LOADED_ITEMS', { queue_id: queueId })
          
          if (vm) {
            vm.$set(response, 'total', queue.total)
            vm.$set(response, 'loaded', queue.loaded)
          }
          
          // Вызываем callback успеха или уведомление (как в download.ts)
          if (item.callback?.success) {
            item.callback.success(item, response)
          } else {
            this.dispatch('file/upload/notifySuccess', { item, response, vm }, { root: true })
          }
          
          if (item.name) {
            commit('CHANGE_COUNTER', { $id: item.name })
          }
        } else {
          throw new Error('Upload failed - no response')
        }
        
      } catch (error: any) {
        console.error(`Upload failed for ${item.name || file?.name}:`, error)
        
        if (item.callback?.error) {
          item.callback.error(item, error)
        } else {
          this.dispatch('file/upload/notifyError', { item, error, vm }, { root: true })
        }
      }
    })
    
    await Promise.all(uploadPromises)
  },
  
  /**
   * Upload files as ArrayBuffer
   */
  async uploadArrayBuffer({ commit, state }: any, { 
    queueId, 
    $id, 
    filetype, 
    vm, 
    params 
  }: { 
    queueId: string; 
    $id?: string; 
    filetype?: string; 
    vm: any; 
    params?: { type?: string; password?: string }
  }) {
    const queue = state.queue[queueId]
    if (!queue) return
    
    const type = params?.type || null
    const password = params?.password || null
    
    const uploadPromises = Object.keys(queue.data).map(async (fileId) => {
      const item = queue.data[fileId]
      const abortController = new AbortController()
      
      if (queue.abortControllers) {
        queue.abortControllers[fileId] = abortController
      }
      
      try {
        const arrayBuffer = await item.file.arrayBuffer()
        
        console.log('ArrayBuffer size:', arrayBuffer.byteLength)
        console.log('Expected file size:', item.size)
        
        if (arrayBuffer.byteLength !== item.size) {
          throw new Error(`File read incomplete: expected ${item.size} bytes, got ${arrayBuffer.byteLength} bytes`)
        }
        
        const headers: Record<string, string> = {
          'Content-Disposition': `attachment; filename="${item.name}"`,
          'Content-Type': 'application/octet-stream',
        }
        
        if (password) {
          headers['x-cert-password'] = password
        }
        
        const response = await $_bridge.sendFileToEnode(arrayBuffer, type, { headers })
        
        if (response) {
          commit('INCREMENT_QUEUE_LOADED_ITEMS', { queue_id: queueId })
          
          if (vm) {
            vm.$set(response, 'total', queue.total)
            vm.$set(response, 'loaded', queue.loaded)
          }
          
          if (item.callback?.success) {
            item.callback.success(item, response)
          } else {
            this.dispatch('file/upload/notifySuccess', { item, response, vm }, { root: true })
          }
          
          if (item.name) {
            commit('CHANGE_COUNTER', { $id: item.name })
          }
        } else {
          throw new Error('Upload failed - no response')
        }
        
      } catch (error: any) {
        console.error(`ArrayBuffer upload failed for ${item.name}:`, error)
        
        if (item.callback?.error) {
          item.callback.error(item, error)
        } else {
          this.dispatch('file/upload/notifyError', { item, error, vm }, { root: true })
        }
      }
    })
    
    await Promise.all(uploadPromises)
  },
  
  /**
   * Cancel specific upload
   */
  cancelUpload({ commit, state }: any, { queueId, fileId }: { queueId: string; fileId: string }) {
    const queue = state.queue[queueId]
    if (queue?.abortControllers?.[fileId]) {
      queue.abortControllers[fileId].abort()
      delete queue.abortControllers[fileId]
      
      if (queue.data[fileId]) {
        Vue.delete(queue.data, fileId)
      }
    }
  },
  
  /**
   * Cancel all uploads in queue
   */
  cancelAllUploads({ commit, state }: any, queueId: string) {
    const queue = state.queue[queueId]
    if (queue?.abortControllers) {
      Object.values(queue.abortControllers).forEach(controller => {
        controller.abort()
      })
      queue.abortControllers = {}
    }
  },
  
  /**
   * Clear queue data
   */
  clearQueue({ commit }: any, queueId: string) {
    commit('CLEAR_QUEUE_DATA', { queue_id: queueId })
  },
  
  /**
   * Change counter
   */
  changeCounter({ commit }: any) {
    commit('CHANGE_COUNTER', { $id: uid('plan') })
  },
  
  // Уведомления (полностью как в download.ts)
  notifyPending({ commit, state }: any, { item, vm }: { item: UploadItemData; vm: any }) {
    const toastId = `toast-${item.id}`
    
    // Если уже создавали тост для этого файла - выходим
    if (pendingCreatedMap.has(toastId)) {
      console.log('Toast already created, skipping:', toastId)
      return
    }
    
    if (vm && vm.$snotify) {
      const existingToast = vm.$snotify.get(toastId)
      if (!existingToast) {
        pendingCreatedMap.set(toastId, true)
        vm.$snotify.html(notifications.uploading(item), {
          id: toastId,
          timeout: 0,
          closeOnClick: true,
          pauseOnHover: false
        })
        console.log('Toast created:', toastId)
      }
    }
  },
  

  notifyProgress({ commit, state }: any, { item, progress, vm }: { item: UploadItemData; progress: number; vm: any }) {
    const toastId = `toast-${item.id}`
    
    if (vm && vm.$snotify) {
      const toast = vm.$snotify.get(toastId)
      
      // Только обновляем существующий тост, не создаём новый!
      if (toast && vm.$snotify.update) {
        vm.$snotify.update(toastId, notifications.progress(item, progress), {
          timeout: 0,
          closeOnClick: true
        })
      }
      // Если тоста нет - НЕ создаём, ждём что он появится от notifyPending
    }
  },
  
  notifySuccess({ commit, state }: any, { item, response, vm }: { item: UploadItemData; response?: any; vm: any }) {
    const oldToastId = `toast-${item.id}`
    const newToastId = `toast-success-${item.id}-${Date.now()}`
    
    if (vm && vm.$snotify) {
      // Удаляем старый тост
      const existingToast = vm.$snotify.get(oldToastId)
      if (existingToast) {
        vm.$snotify.remove(oldToastId)
      }
      
      // Создаём новый тост успеха
      vm.$snotify.html(notifications.success(item), {
        id: newToastId,
        timeout: 3000,
        closeOnClick: true
      })
    } else {
      console.log('Upload completed for:', item.name)
    }
  },
  
  notifyError({ commit, state }: any, { item, error, vm }: { item: UploadItemData; error: any; vm: any }) {
    const oldToastId = `toast-${item.id}`
    const newToastId = `toast-error-${item.id}-${Date.now()}`
    const errorMsg = error.message || 'Неизвестная ошибка'
    
    if (vm && vm.$snotify) {
      const existingToast = vm.$snotify.get(oldToastId)
      if (existingToast) {
        vm.$snotify.remove(oldToastId)
      }
      
      vm.$snotify.html(notifications.error(item, errorMsg), {
        id: newToastId,
        timeout: 3000,
        closeOnClick: true
      })
    } else {
      console.error(`Upload failed for ${item.name}:`, errorMsg)
    }
  }
}

// Экспорт модуля
export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}